import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { CreateTaskDto, GroupBy, MoveTaskDto, Task, UpdateTaskDto } from '@/types';

export function useCatalogs() {
  return useQuery({
    queryKey: ['catalogs'],
    queryFn: api.catalogs,
    staleTime: 5 * 60 * 1000
  });
}

export function useTasks(groupBy: GroupBy, search = '', opts?: { refetchInterval?: number }) {
  return useQuery({
    queryKey: ['tasks', groupBy, search],
    queryFn: () => api.tasks.list({ groupBy, search: search || undefined }),
    // refetchInterval opcional: el modo planning lo usa para refrescar en vivo
    // (varios planificando a la vez). Sin él, refetch normal por staleness/focus.
    refetchInterval: opts?.refetchInterval,
  });
}

export function useTaskDetail(id: number | null) {
  return useQuery({
    queryKey: ['task', id],
    queryFn: () => api.tasks.get(id!),
    enabled: id !== null
  });
}

export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (dto: CreateTaskDto) => api.tasks.create(dto),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks'] })
  });
}

export function useUpdateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: UpdateTaskDto }) => api.tasks.update(id, dto),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: ['tasks'] });
      qc.invalidateQueries({ queryKey: ['task', id] });
    }
  });
}

export function useDeleteTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.tasks.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tasks'] })
  });
}

export function useMoveTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: MoveTaskDto }) => api.tasks.move(id, dto),
    onMutate: async ({ id, dto }) => {
      await qc.cancelQueries({ queryKey: ['tasks'] });
      const previous = qc.getQueriesData<Task[]>({ queryKey: ['tasks', dto.groupBy] });
      qc.setQueriesData<Task[]>({ queryKey: ['tasks', dto.groupBy] }, (old) => {
        if (!old) return old;
        return old.map(t => {
          if (t.idTareaArq !== id) return t;
          const u = { ...t };
          if (dto.targetGroupId !== undefined && dto.targetGroupId !== null) {
            if (dto.groupBy === 'status') u.idEstado = dto.targetGroupId;
            if (dto.groupBy === 'assignee') u.responsable = dto.targetGroupId;
            if (dto.groupBy === 'project') u.idProyecto = dto.targetGroupId;
          }
          const newPos = ((dto.beforePosition ?? 0) + (dto.afterPosition ?? (dto.beforePosition ?? 0) + 2000)) / 2;
          if (dto.groupBy === 'status') u.positionByStatus = newPos;
          if (dto.groupBy === 'assignee') u.positionByAssignee = newPos;
          if (dto.groupBy === 'project') u.positionByProject = newPos;
          return u;
        });
      });
      return { previous };
    },
    onError: (_e, _v, ctx) => ctx?.previous.forEach(([k, d]) => qc.setQueryData(k, d)),
    onSettled: () => qc.invalidateQueries({ queryKey: ['tasks'] })
  });
}

export function useBitacora(taskId: number | null) {
  return useQuery({
    queryKey: ['bitacora', taskId],
    queryFn: () => api.bitacora.list(taskId!),
    enabled: taskId !== null
  });
}

export function useChecklist(taskId: number | null) {
  return useQuery({
    queryKey: ['checklist', taskId],
    queryFn: () => api.checklist.list(taskId!),
    enabled: taskId !== null
  });
}
